/**
 * Papers on the file — the per-file intake. One file is one document.
 *
 * Most cases arrive mid-flight: the office keeps a folder per case, every
 * document filed properly, and an imported case brings its whole history. So
 * the load-bearing rule of this screen is the ruled one: **adding a paper and
 * naming what it is records that document on the case** — the register stops
 * saying "draft the noting sheet" on a case whose noting sheet is right here.
 *
 * **The screen is the folder and the way to add to it, and nothing else.**
 * It carried two more sections — the machine's subject-and-gist draft, and the
 * intake check list. The first is settled on the case screen, which is where
 * an officer reads it; showing it here too meant the same two Hindi sentences
 * appeared four times on one page. The second reported catalogue rows, passes
 * included, that an officer cannot act on from here. Neither is hidden by
 * being absent: the checks still bite where they are enforced, and the subject
 * and gist are still confirmed where they belong.
 *
 * Three things are the product rather than the layout:
 *
 * **Nothing is refused for what the case holds.** A signed paper that exists
 * is recorded (stop list 5). A check never gates the upload.
 *
 * **A paper is named once, at upload.** The row carried a second label form,
 * so the same act had two doors and the list carried a form per paper. A
 * paper named wrongly is removed and added again under the right name.
 *
 * **The upload asks for two things: the file, and what it is.** It asked for
 * the paper's own date too, which an officer typed fifteen times for a folder
 * that already carries its dates in its filenames — the detector reads them,
 * and a paper whose name holds none has no date rather than a guessed one.
 * The `Added` column is the day the paper reached the file, which is a fact
 * about the folder; the paper's own date is a fact about the document, and
 * generation reads that one.
 *
 * **Stacked authorship.** Where the officer's date and the one read off the
 * filename differ, both stay on screen with who adjusted it (rule 4). Where
 * they agree there is no stack, and nothing is said.
 *
 * **The one-document question asks; it never refuses.** A detection that a
 * file looks like several documents renders as a question with Yes and No,
 * and either answer settles it as a recorded decision (IC-FILE-02, CONFIRM).
 *
 * **Blocked actions stay on screen with the rule.** Remove on a recorded
 * paper is greyed carrying the reason — withdraw the document instead.
 *
 * No document title or stage name is written in this file — the type list
 * comes off the case's own rail, which comes off the pinned process version
 * (stop list 6).
 */

import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type {
  CaseDetail,
  RecordPaperKind,
  PaperRecordView,
  SourceFileReceipt,
  SourceFileView,
} from '../api/client'
import { ErrorEnvelope } from '../components/bits'
import { readDay } from '../lib/blocks'
import { readDate } from '../lib/period'
import { KindLine, WhatTheAppRead } from './papers/WhatTheAppRead'
import { READING_LIVE } from './papers/reading'

/** The free-text class for a paper that is none of the twenty. */
const OTHER = '__other__'

/** Read states in which the worker is still doing something, so the list polls. */
const READING = new Set(['QUEUED', 'CHUNKING', 'OCR', 'ANALYSING'])

export function Papers() {
  const { caseId } = useParams()
  const client = useQueryClient()

  const detail = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase(caseId as string),
    enabled: Boolean(caseId),
  })
  const papers = useQuery({
    queryKey: ['papers', caseId],
    queryFn: () => api.listSourceFiles(caseId as string),
    enabled: Boolean(caseId),
    // Poll only while a read is actually running — the OCR's, or the reading
    // of what the paper says. A list sitting still under the officer's hands
    // would lose the row they were part-way through.
    refetchInterval: (query) =>
      (query.state.data?.files ?? []).some(
        (f) => READING.has(f.read_state ?? '') || READING_LIVE.has(f.reading_state ?? ''),
      )
        ? 2500
        : false,
  })
  /* What each paper says, read once (docs/19 A6) — one request for the whole
     file, polled while any paper is still being read. */
  const record = useQuery({
    queryKey: ['record', caseId],
    queryFn: () => api.getCaseRecord(caseId as string),
    enabled: Boolean(caseId),
    refetchInterval: (query) =>
      (query.state.data?.papers ?? []).some((p) => READING_LIVE.has(p.latest?.state ?? ''))
        ? 3000
        : false,
  })
  const kinds = record.data?.kinds ?? []
  const recordFor = (fileId: string) =>
    record.data?.papers.find((paper) => paper.source_file_id === fileId)

  const [failure, setFailure] = useState<unknown>(null)

  // **The list is the receipt.** A banner announced each upload — *Recorded
  // D04 · occurrence 1 on the case* — above a list that had just grown the row
  // saying the same thing, named and dated, and stayed there until the next
  // act pushed it out. A failure still speaks; a success is visible.
  const landed = (_result: SourceFileReceipt) => {
    setFailure(null)
    void client.invalidateQueries({ queryKey: ['papers', caseId] })
    void client.invalidateQueries({ queryKey: ['case', caseId] })
    void client.invalidateQueries({ queryKey: ['record', caseId] })
  }

  if (papers.isLoading || detail.isLoading) {
    return <div className="empty">Reading the papers…</div>
  }
  if (papers.error) {
    return (
      <div className="page">
        <ErrorEnvelope error={papers.error} />
        <Link to={`/cases/${caseId}`}>Back to the case</Link>
      </div>
    )
  }
  const view = papers.data
  if (!view) return <div className="empty">No such case.</div>

  const active = view.files.filter((f) => !f.removed_at)
  const removed = view.files.filter((f) => f.removed_at)

  return (
    <div className="page">
      <header>
        <div>
          <h1>Papers on the file</h1>
          <p className="mono small muted">
            {detail.data ? (detail.data.file_number ?? 'Not yet numbered') : null}
          </p>
        </div>
        <Link to={`/cases/${caseId}`}>Back to the case</Link>
      </header>

      {/* **Two things, in the order they are wanted.** Archit, 5 Sep: this
          screen is *"usually to see which files are there, and if they want to
          add or remove any document."* So: the folder, then the way to add to
          it. The machine's subject-and-gist draft and the check list were the
          other two sections and both have gone — the first is settled on the
          case screen, which is where it is read, and the second reports
          catalogue rows an officer cannot act on here. */}
      {failure ? <ErrorEnvelope error={failure} /> : null}

      <section className="papers-list">
        <h3>
          On the file
          <span className="cnt mono">
            {active.length} paper{active.length === 1 ? '' : 's'}
          </span>
        </h3>
        <div>
          {active.length === 0 ? (
            <p className="muted">Nothing on the file yet.</p>
          ) : (
            <>
              {/* The column heads are the row's own grid, so a filename and
                  what it was recorded as line up down the list instead of
                  being tracked across each row separately. */}
              <div className="paper-line paper-head small muted" aria-hidden="true">
                <span>Paper</span>
                <span>File</span>
                <span>What it is</span>
                <span>Added</span>
                <span />
              </div>
              {active.map((row) => (
                <PaperRow
                  key={row.id}
                  caseId={caseId as string}
                  row={row}
                  detail={detail.data}
                  record={recordFor(row.id)}
                  kinds={kinds}
                  onDone={landed}
                  onError={setFailure}
                />
              ))}
            </>
          )}
        </div>
      </section>

      <AddPaper
        caseId={caseId as string}
        detail={detail.data}
        onDone={landed}
        onError={setFailure}
      />

      {removed.length > 0 ? (
        <details className="fold">
          <summary>
            Removed papers
            <span className="cnt mono">{removed.length}</span>
          </summary>
          <div className="fold-body">
            {removed.map((row) => (
              <p key={row.id} className="small muted">
                <span className="mono">{row.filename}</span> — removed by {row.removed_by}:{' '}
                {row.removal_reason}
              </p>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------- add a paper */

function AddPaper({
  caseId,
  detail,
  onDone,
  onError,
}: {
  caseId: string
  detail: CaseDetail | undefined
  onDone: (result: SourceFileReceipt) => void
  onError: (failure: unknown) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [type, setType] = useState('')
  const [description, setDescription] = useState('')

  const types = useMemo(() => documentTypes(detail), [detail])
  /* **An officer cannot type Devanagari.** Every paper the office receives —
     the complaint, a पावती, the written statement, the appeal — is *Other*
     plus a Hindi name, and the keyboards are English. So the names are offered
     rather than typed, off the same endpoint the register's own titles come
     from. `Other` stays for the paper nobody foresaw. */
  const supporting = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })
  const papers = supporting.data?.supporting_papers ?? []

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.uploadSourceFile(caseId, file, {
        document_type: type && !type.startsWith(OTHER) ? type : undefined,
        other_description: type.startsWith(`${OTHER}:`)
          ? type.slice(OTHER.length + 1)
          : type === OTHER && description
            ? description
            : undefined,
      }),
    onSuccess: (result) => {
      setType('')
      setDescription('')
      if (fileInput.current) fileInput.current.value = ''
      onDone(result)
    },
    onError,
  })

  return (
    /* **Not a fold.** It was one, opened on an empty file and closed once
       there was a list to read past — so on every later visit the one act this
       screen offers sat behind a click. The page is the folder and the way to
       add to it; both stay on screen. */
    <section className="card add-paper" data-testid="add-paper-fold">
      <header>
        <span>Add a paper</span>
      </header>
      <div className="body">
        <div className="row form">
          <div className="field">
            <label htmlFor="paper-file">The scan</label>
            <input id="paper-file" ref={fileInput} type="file" disabled={upload.isPending} />
          </div>
          <div className="field">
            <label htmlFor="paper-type">What it is</label>
            <select
              id="paper-type"
              aria-label="What it is"
              value={type}
              onChange={(event) => setType(event.target.value)}
            >
              <option value="">Not yet named</option>
              <optgroup label="Documents the section writes">
                {types.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Papers the file receives">
                {papers.map((option) => (
                  <option key={option.name_hi} value={`${OTHER}:${option.name_hi}`}>
                    {option.name_en} — {option.name_hi}
                  </option>
                ))}
                <option value={OTHER}>Something else</option>
              </optgroup>
            </select>
          </div>
          {type === OTHER ? (
            <div className="field">
              <label htmlFor="paper-description">What the office calls it</label>
              <input
                id="paper-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          ) : null}
          <button
            className="btn primary"
            data-testid="add-paper"
            disabled={upload.isPending}
            onClick={() => {
              const file = fileInput.current?.files?.[0]
              if (file) upload.mutate(file)
            }}
          >
            {upload.isPending ? 'Adding…' : 'Add the paper'}
          </button>
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------- paper row */

/** What a supporting paper is called on screen.
 *
 * The office calls it पावती, and that is what gets stored and what the drafter
 * cites — but the interface is English (rule 5), and an officer who cannot type
 * Devanagari cannot scan a column of it either. So the English name is shown
 * and the Hindi is what the record keeps. A paper named through *Something
 * else* shows whatever was typed, which is the officer's own words.
 */
function supportingName(
  stored: string | null | undefined,
  papers: { name_hi: string; name_en: string }[],
): string {
  if (!stored) return ''
  return papers.find((p) => p.name_hi === stored)?.name_en ?? stored
}

function PaperRow({
  caseId,
  row,
  detail,
  record,
  kinds,
  onDone,
  onError,
}: {
  caseId: string
  row: SourceFileView
  detail: CaseDetail | undefined
  record: PaperRecordView | undefined
  kinds: RecordPaperKind[]
  onDone: (result: SourceFileReceipt) => void
  onError: (failure: unknown) => void
}) {
  const types = useMemo(() => documentTypes(detail), [detail])
  const titleFor = (value: string | null | undefined) =>
    types.find((t) => t.value === value)?.label ?? value ?? null
  const process = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })
  const papers = process.data?.supporting_papers ?? []

  const label = useMutation({
    mutationFn: (body: Parameters<typeof api.labelSourceFile>[2]) =>
      api.labelSourceFile(caseId, row.id, body),
    onSuccess: onDone,
    onError,
  })
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const remove = useMutation({
    mutationFn: (why: string) =>
      api.removeSourceFile(caseId, row.id, { preflight: false, reason: why }),
    onSuccess: (result) => {
      setAsking(false)
      setReason('')
      onDone(result)
    },
    onError,
  })

  const filed = Boolean(row.document_instance_id)
  // A withdrawn document no longer stands — rendering its paper as a live
  // recording would assert something the register denies, and greying Remove
  // for a rule that stopped holding is the same lie the other way.
  const withdrawn = row.document_state === 'WITHDRAWN'
  const standing = filed && !withdrawn
  const asksOneDocument = row.detected_multi_document === true && !row.officer_disposition

  /* **What the paper is — once, in words.** The column read *not yet named*
     over a line saying *Kind: Written statement of defence*: the column knew
     only the name given at upload, and a paper said to be something on the
     case page has a kind and no such name (walkthrough 32.34). And where it
     did have a name it led with the register's code — `D04 ·`.

     So the column says it, from whichever the officer gave: the register
     document it was filed as, by that document's own name; else the kind they
     said it is; else the name they typed. *Not yet named* is left for the
     paper nobody has said anything about — the app's guess is not the
     officer's word (rule 4) and stays on the line below, to confirm. And
     because the column says it, the line below does not say it again. */
  const kindName = row.paper_kind
    ? (kinds.find((kind) => kind.key === row.paper_kind)?.label_en ?? null)
    : null
  const what: string | null = row.document_type
    ? titleFor(row.document_type)
    : (kindName ??
      (row.other_description ? supportingName(row.other_description, papers) : null))

  /* **Naming happens at upload, and nowhere else.** The row carried its own
     label form — a type list, a date and a button — plus four lines of prose
     about how the text was read and what had not been checked. Six lines per
     paper, on a screen whose job is to show a folder. A paper named wrongly
     is removed and added again under the right name, which is one act the
     officer already knows rather than a second way to do the same thing. */
  return (
    <div className="paper" data-testid={`paper-${row.ordinal}`}>
      <div className="paper-line small">
        <span className="muted mono">{row.ordinal}</span>
        <a
          className="mono"
          href={api.sourceFilePath(caseId, row.id)}
          target="_blank"
          rel="noreferrer"
        >
          {row.filename}
        </a>
        {standing ? (
          <span data-testid="paper-recorded">{what}</span>
        ) : withdrawn ? (
          <span className="muted" data-testid="paper-withdrawn">
            Was {what} — withdrawn
          </span>
        ) : what ? (
          <span title={row.other_description ?? undefined} data-testid="paper-what">
            {what}
          </span>
        ) : (
          <span className="muted">not yet named</span>
        )}
        {/* `created_at` is a timestamp — one of the few the record keeps — and
            `readDate` takes a date. Handed the whole thing it cannot parse the
            day and hands the ISO string back, time and zone included, which is
            what this column was showing. `readDay` is the app's own step down
            from a timestamp to a day, and it is the local day: slicing the ISO
            string would put a paper added at two in the morning on the day
            before. */}
        <span className="mono muted">{readDate(readDay(row.created_at))}</span>
        {/* Greyed only while the recorded document stands: the rule travels
            with the button, and stops applying when the register says the
            document is withdrawn. */}
        <button
          className="btn ghost"
          data-testid={`remove-paper-${row.ordinal}`}
          disabled={standing || remove.isPending}
          title={
            standing
              ? 'This paper is recorded on the case as a document, and the document stands. Withdraw the document itself first; once withdrawn, the paper can be removed with its reason.'
              : 'Remove this paper — then add it again under the right name.'
          }
          onClick={() => setAsking(true)}
        >
          Remove
        </button>
      </div>

      {/* **The reason is asked on the row, not by the browser.** A paper off
          the file states why and the row stays with it, so the reason is not
          a confirmation to click past — it is the record. A native prompt
          could not be styled, could not show the filename it was about, and
          read as the page having lost control of itself. */}
      {asking ? (
        <div className="row form" data-testid={`remove-reason-${row.ordinal}`}>
          <div className="field">
            <label htmlFor={`remove-why-${row.id}`}>Why is it coming off the file?</label>
            <input
              id={`remove-why-${row.id}`}
              value={reason}
              autoFocus
              placeholder="named wrongly — adding it again"
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && reason.trim()) remove.mutate(reason.trim())
                if (event.key === 'Escape') setAsking(false)
              }}
            />
          </div>
          <button
            className="btn"
            disabled={!reason.trim() || remove.isPending}
            onClick={() => remove.mutate(reason.trim())}
          >
            {remove.isPending ? 'Removing…' : 'Remove the paper'}
          </button>
          <button className="btn" disabled={remove.isPending} onClick={() => setAsking(false)}>
            Keep it
          </button>
        </div>
      ) : null}

      <ReadState caseId={caseId} row={row} onError={onError} />

      {/* What kind of paper it is — the officer's word, or the app's guess to
          confirm — and what the app read from it, each item with its page. */}
      <KindLine
        caseId={caseId}
        row={row}
        kinds={kinds}
        // What the row above said the paper is, so the line does not repeat it.
        said={row.document_type ? 'document' : kindName ? 'kind' : undefined}
        onError={onError}
      />
      <WhatTheAppRead
        caseId={caseId}
        row={row}
        record={record}
        kinds={kinds}
        onError={onError}
      />

      {/* **Rule 4, and only when it has something to say.** A date the machine
          read off the filename and the officer's date are the same on almost
          every paper; the stack matters where they differ. */}
      {row.document_date_adjusted_by ? (
        <p className="small muted">
          Detected from the filename{' '}
          {row.document_date_detected ? (
            <span className="mono">{readDate(row.document_date_detected)}</span>
          ) : null}{' '}
          · adjusted by {row.document_date_adjusted_by}
        </p>
      ) : null}

      {asksOneDocument ? (
        <div className="note small" data-testid="one-document-question">
          <p>
            This file looks like more than one document — {row.detected_reason} Is that right?
          </p>
          <div className="row form">
            <button
              className="btn"
              disabled={label.isPending}
              onClick={() =>
                label.mutate({
                  preflight: false,
                  officer_disposition: 'one document — confirmed',
                })
              }
            >
              It is one document
            </button>
            <button
              className="btn"
              disabled={label.isPending}
              onClick={() =>
                label.mutate({
                  preflight: false,
                  officer_disposition: 'several documents — will re-scan',
                })
              }
            >
              It is several
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * A read that needs attention — and silence when it does not.
 *
 * This was a column: `proofread` on a Word file, `4 pp` on a read scan. Both
 * say a read finished, which is the ordinary case and not news, so the column
 * was a word on every row telling the officer nothing had gone wrong. It is
 * gone.
 *
 * What is left is the three states that are not ordinary, and each carries
 * the one action that answers it: a read that never started, one still
 * running, one that failed, and a file the pipeline cannot open at all.
 * Nothing is hidden by the settled states being quiet — a paper with no line
 * under it has been read.
 */
function ReadState({
  caseId,
  row,
  onError,
}: {
  caseId: string
  row: SourceFileView
  onError: (failure: unknown) => void
}) {
  const client = useQueryClient()
  const retry = useMutation({
    mutationFn: () => api.retryBundle(caseId, row.ingestion_job_id as string),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['papers', caseId] })
    },
    onError,
  })

  if (row.removed_at) return null
  if (!row.ingestion_job_id) {
    return (
      <p className="small muted">
        No text can be read from this file — it is stored and filed, but it will not reach the
        drafter or Ask.
      </p>
    )
  }
  // A Word file's text arrived at upload, proofread by an officer. Nothing to
  // report and nothing to do.
  if (row.text_origin === 'DOCX_UPLOAD') return null

  const state = row.read_state ?? ''
  if (state === 'QUEUED') {
    // Queued is not reading: if the queue was down at upload, nothing sweeps
    // the row up later, so the exit is offered here rather than implied.
    return (
      <p className="small muted" data-testid={`read-queued-${row.ordinal}`}>
        Waiting to be read.{' '}
        <button className="btn ghost" disabled={retry.isPending} onClick={() => retry.mutate()}>
          {retry.isPending ? 'Re-queuing…' : 'Nudge the queue'}
        </button>
      </p>
    )
  }
  if (READING.has(state)) {
    return (
      <p className="small muted" data-testid={`read-running-${row.ordinal}`}>
        Being read · {row.read_pages_done} of {row.read_pages_total ?? '—'} page
        {row.read_pages_total === 1 ? '' : 's'}.
      </p>
    )
  }
  if (state === 'FAILED') {
    return (
      <p className="small" data-testid={`read-failed-${row.ordinal}`}>
        Could not be read: {row.read_error}{' '}
        <button className="btn ghost" disabled={retry.isPending} onClick={() => retry.mutate()}>
          {retry.isPending ? 'Re-queuing…' : 'Read it again'}
        </button>
      </p>
    )
  }
  return null
}

/**
 * The type list off the case's own rail — the pinned version, never a literal.
 *
 * By name, with no register code in front: `D04 ·` is the data model's number
 * for a document, and nobody at a desk calls the enquiry report that. The list
 * keeps the rail's own order, which is the order of the proceeding.
 */
function documentTypes(detail: CaseDetail | undefined): { value: string; label: string }[] {
  if (!detail) return []
  return detail.rail.map((row) => ({
    value: row.document_type,
    label: row.short_title ?? row.title,
  }))
}
